import { wpQuery } from './wp'
import type { DirectionPageProps } from '@pages/DirectionPage'

// ── Raw GraphQL shape ─────────────────────────────────────────────────────────

interface WpImageNode {
  node: { sourceUrl: string } | null
}

interface WpDirectionData {
  name: string | null
  cover: WpImageNode | null
  subtitle: string | null
  description: string | null
  dev: boolean | null
}

interface WpDirectionBlocks {
  showServices: boolean | null
  services: string | null
  showStages: boolean | null
  stages: string | null
  showAdvantages: boolean | null
  advantages: string | null
  showCertificates: boolean | null
  certificates: string | null
  showPhotos: boolean | null
  photos: string | null
}

interface WpCaseNode {
  slug: string
  caseData: { name: string | null; year: number | null; cover: WpImageNode | null } | null
}

interface WpDirectionPost {
  title: string
  slug: string
  directionData: WpDirectionData | null
  directionBlocks?: WpDirectionBlocks | null
}

interface WpDirectionBySlugResponse {
  direction: WpDirectionPost | null
  directionTax?: { cases?: { nodes: WpCaseNode[] } } | null
}

interface WpDirectionsListResponse {
  directions: { nodes: { title: string; slug: string; menuOrder: number | null; directionData: { name: string | null; cover: WpImageNode | null; subtitle: string | null } | null }[] }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function imgUrl(img: WpImageNode | null | undefined): string | undefined {
  return img?.node?.sourceUrl ?? undefined
}

function splitLines(raw: string | null): string[] {
  if (!raw) return []
  return raw.split('\n').map(s => s.replace(/\r$/, '').trim()).filter(Boolean)
}

function splitPairs(raw: string | null): { title: string; desc: string }[] {
  return splitLines(raw).map(line => {
    const sep = line.indexOf('|')
    if (sep === -1) return { title: line, desc: '' }
    return { title: line.slice(0, sep).trim(), desc: line.slice(sep + 1).trim() }
  }).filter(p => p.title)
}

function parsePhotoList(raw: string | null): { caption: string; photo?: string }[] {
  return splitLines(raw).map(line => {
    const sep = line.indexOf('|')
    if (sep === -1) {
      // just a URL, no caption
      return { caption: '', photo: line.trim() }
    }
    const url = line.slice(0, sep).trim()
    const caption = line.slice(sep + 1).trim()
    return { caption, ...(url ? { photo: url } : {}) }
  }).filter(p => p.photo || p.caption)
}

function wpDirectionToProps(
  post: WpDirectionPost,
  cases: WpCaseNode[],
): DirectionPageProps {
  const d = post.directionData
  const b = post.directionBlocks
  return {
    slug: post.slug,
    title: d?.name ?? post.title,
    cover: imgUrl(d?.cover),
    subtitle: d?.subtitle ?? undefined,
    description: splitLines(d?.description ?? null),
    services: splitLines(b?.services ?? null),
    stages: splitPairs(b?.stages ?? null),
    advantages: splitPairs(b?.advantages ?? null),
    certificates: parsePhotoList(b?.certificates ?? null).map(p => ({ label: p.caption, photo: p.photo })),
    photos: parsePhotoList(b?.photos ?? null),
    projects: cases.map(c => ({
      slug: c.slug,
      title: c.caseData?.name ?? c.slug,
      year: c.caseData?.year != null ? String(c.caseData.year) : '',
      photo: imgUrl(c.caseData?.cover),
    })),
    showServices:      b?.showServices      ?? true,
    showStages:        b?.showStages        ?? true,
    showAdvantages:    b?.showAdvantages    ?? true,
    showCertificates:  b?.showCertificates  ?? true,
    showPhotos:        b?.showPhotos        ?? true,
    dev:               d?.dev               ?? false,
  }
}

// ── GraphQL fragments ─────────────────────────────────────────────────────────

const IMG = `{ node { sourceUrl } }`

const DIRECTION_DATA_FIELDS = /* graphql */ `
  directionData {
    name cover ${IMG}
    subtitle description
    dev
  }
`

const DIRECTION_BLOCKS_FIELDS = /* graphql */ `
  directionBlocks {
    showServices services
    showStages stages
    showAdvantages advantages
    showCertificates certificates
    showPhotos photos
  }
`

// ── Public API ────────────────────────────────────────────────────────────────

const CASE_FIELDS = /* graphql */ `
  slug
  caseData { name year cover { node { sourceUrl } } }
`

export async function fetchDirectionBySlug(slug: string): Promise<DirectionPageProps | null> {
  const data = await wpQuery<WpDirectionBySlugResponse>(
    /* graphql */ `
    query DirectionBySlug($slug: ID!) {
      direction(id: $slug, idType: SLUG) {
        title slug
        ${DIRECTION_DATA_FIELDS}
        ${DIRECTION_BLOCKS_FIELDS}
      }
      directionTax(id: $slug, idType: SLUG) {
        cases(first: 20) {
          nodes { ${CASE_FIELDS} }
        }
      }
    }
  `,
    { slug },
  )
  if (!data.direction) return null
  return wpDirectionToProps(data.direction, data.directionTax?.cases?.nodes ?? [])
}

export interface DirectionListItem {
  slug: string
  name: string
  cover?: string
  subtitle?: string
}

const directionListCache = new Map<string, Promise<DirectionListItem[]>>()

export function fetchDirectionList(language: string = 'RU'): Promise<DirectionListItem[]> {
  let promise = directionListCache.get(language)
  if (!promise) {
    promise = wpQuery<WpDirectionsListResponse>(/* graphql */ `
      query DirectionList($language: LanguageCodeFilterEnum) {
        directions(first: 20, where: { language: $language }) {
          nodes {
            title slug menuOrder
            directionData { name cover ${IMG} subtitle }
          }
        }
      }
    `, { language }).then(data => [...data.directions.nodes]
      .sort((a, b) => (a.menuOrder ?? 0) - (b.menuOrder ?? 0))
      .map(p => ({
        slug: p.slug,
        name: p.directionData?.name ?? p.title,
        cover: imgUrl(p.directionData?.cover),
        subtitle: p.directionData?.subtitle ?? undefined,
      }))).catch(err => {
      directionListCache.delete(language)
      throw err
    })
    directionListCache.set(language, promise)
  }
  return promise
}
